/* ================================================================
   NewJaisa — Student Experience
   File    : assets/student-renderer.js
   Purpose : Populates EXISTING placeholders inside the
             student-experience.liquid markup with data from
             STUDENT_CONFIG. Never creates, clones, or injects
             new HTML elements — only updates:
             - text
             - images (src / alt)
             - links (href / text)
             - CSS variables
             - visibility of pre-built fixed-slot list items
   Exposes : window.StudentRenderer
   ================================================================ */

window.StudentRenderer = (function () {
  'use strict';

  /* ---------------------------------------------------------
     Small safe-DOM helpers — every one guards against a
     missing element so a partial config never throws.
     --------------------------------------------------------- */

  function setText(root, selector, value) {
    if (!value) return;
    var el = root.querySelector(selector);
    if (el) el.textContent = value;
  }

  function setImage(root, selector, src, alt) {
    var el = root.querySelector(selector);
    if (!el) return;

    if (!src) {
      el.hidden = true;
      return;
    }

    el.src = src;
    if (alt) el.alt = alt;
    el.hidden = false;
  }

  function setLink(root, selector, href, text) {
    var el = root.querySelector(selector);
    if (!el) return;

    if (href) el.href = href;
    if (text) el.textContent = text;
  }

  /**
   * Fills a fixed-slot repeating list (benefits, accessories,
   * placement technologies, FAQ, success stories). Extra
   * pre-built slots beyond the config array length are hidden,
   * never removed.
   */
  function fillList(root, itemSelector, items, fillItemFn) {
    var slots = root.querySelectorAll(itemSelector);

    slots.forEach(function (slot, index) {
      var data = items && items[index];

      if (!data) {
        slot.hidden = true;
        return;
      }

      slot.hidden = false;
      fillItemFn(slot, data);
    });
  }

  /* ---------------------------------------------------------
     Section renderers
     --------------------------------------------------------- */

  /**
   * Applies the per-college accent theme by overriding the CSS
   * custom properties already declared on .nj-student in
   * student-mode.css. This only ever sets inline style
   * properties on the existing root element — it never changes
   * markup, classes, or adds new nodes.
   */
  function renderAccentTheme(root, config) {
    var theme = config.accentTheme;
    if (!theme) return;

    if (theme.primary) root.style.setProperty('--nj-primary', theme.primary);
    if (theme.secondary) root.style.setProperty('--nj-secondary', theme.secondary);
    if (theme.accent) root.style.setProperty('--nj-accent', theme.accent);
    if (theme.highlight) root.style.setProperty('--nj-highlight', theme.highlight);
  }

  function renderHero(root, config) {
    setText(root, '[data-nj-badge]', config.hero.badgeText);
    setText(root, '[data-nj-hero-title]', config.hero.title);
    setText(root, '[data-nj-hero-subtitle]', config.hero.subtitle);
    setImage(root, '[data-nj-hero-logo]', config.hero.logoImage, config.hero.logoAlt);

    if (config.hero.backgroundGradient) {
      var heroEl = root.querySelector('[data-nj-hero]');
      if (heroEl) heroEl.style.background = config.hero.backgroundGradient;
    }
  }

  function renderBenefits(root, config) {
    var benefits = config.benefits;
    if (!benefits) return;

    setText(root, '[data-nj-benefits-heading]', benefits.headingText);
    setText(root, '[data-nj-benefits-value]', benefits.totalValueText);

    fillList(root, '[data-nj-benefit-item]', benefits.items, function (slot, item) {
      setImage(slot, '[data-nj-benefit-icon]', item.iconImage, item.title);
      setText(slot, '[data-nj-benefit-title]', item.title);
      setText(slot, '[data-nj-benefit-desc]', item.description);
    });
  }

  function renderCoupon(root, config) {
    var coupon = config.coupon;
    if (!coupon) return;

    setText(root, '[data-nj-coupon-code]', coupon.code);
    setText(root, '[data-nj-coupon-discount]', coupon.discountText);
    setText(root, '[data-nj-coupon-desc]', coupon.description);
    setText(root, '[data-nj-coupon-expiry]', coupon.expiryText);

    var copyBtn = root.querySelector('[data-nj-coupon-copy]');
    var codeEl = root.querySelector('[data-nj-coupon-code]');

    if (copyBtn && codeEl && !copyBtn.dataset.njBound) {
      copyBtn.dataset.njBound = 'true';
      copyBtn.addEventListener('click', function () {
        var code = codeEl.textContent;
        if (!code) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(function () {
            announceCopy(copyBtn);
          });
        } else {
          announceCopy(copyBtn);
        }
      });
    }
  }

  function announceCopy(button) {
    var original = button.getAttribute('data-nj-copy-label') || button.textContent;
    button.setAttribute('data-nj-copy-label', original);
    button.textContent = 'Copied!';

    window.setTimeout(function () {
      button.textContent = original;
    }, 1800);
  }

  function renderEmi(root, config) {
    var emi = config.emi;
    if (!emi) return;

    setText(root, '[data-nj-emi-title]', emi.title);
    setText(root, '[data-nj-emi-desc]', emi.description);
    setText(root, '[data-nj-emi-tenure]', emi.minTenureText);

    var highlightEl = root.querySelector('[data-nj-emi-highlight]');
    if (highlightEl) {
      if (emi.highlightText) {
        highlightEl.textContent = emi.highlightText;
        highlightEl.hidden = false;
      } else {
        highlightEl.hidden = true;
      }
    }
  }

  function renderServiceCenter(root, config) {
    var center = config.serviceCenter;
    if (!center) return;

    setText(root, '[data-nj-service-center-title]', center.title);
    setText(root, '[data-nj-service-center-name]', center.name);
    setText(root, '[data-nj-service-center-city]', center.cityLabel);
    setText(root, '[data-nj-service-center-address]', center.address);
    setText(root, '[data-nj-service-center-hours]', center.hours);

    if (center.phone) {
      setLink(root, '[data-nj-service-center-call]', 'tel:+' + center.phone, null);
    }
    if (center.whatsappNumber) {
      setLink(root, '[data-nj-service-center-whatsapp]', 'https://wa.me/' + center.whatsappNumber, null);
    }
    if (center.mapsUrl) {
      setLink(root, '[data-nj-service-center-maps]', center.mapsUrl, null);
    }
  }

  function renderSupport(root, config) {
    var support = config.support;
    if (!support) return;

    setText(root, '[data-nj-support-title]', support.title);
    setText(root, '[data-nj-support-desc]', support.description);
    setText(root, '[data-nj-support-response]', support.responseTimeText);
    setLink(root, '[data-nj-support-link]', support.whatsappLink, null);

    if (support.callNumber) {
      setLink(root, '[data-nj-support-call]', 'tel:+' + support.callNumber, null);
    }
  }

  function renderDelivery(root, config) {
    var delivery = config.delivery;
    if (!delivery) return;

    setText(root, '[data-nj-delivery-title]', delivery.title);
    setText(root, '[data-nj-delivery-desc]', delivery.description);
    setText(root, '[data-nj-delivery-days]', delivery.estimatedDaysText);
  }

  function renderPlacement(root, config) {
    var placement = config.placement;
    if (!placement) return;

    setText(root, '[data-nj-placement-title]', placement.title);
    setText(root, '[data-nj-placement-desc]', placement.description);

    fillList(root, '[data-nj-placement-item]', placement.technologies, function (slot, techName) {
      slot.textContent = techName;
    });
  }

  function renderSuccessStories(root, config) {
    var stories = config.successStories;
    var section = root.querySelector('[data-nj-success-stories-section]');
    if (!section) return;

    if (!stories || !stories.length) {
      section.hidden = true;
      return;
    }

    section.hidden = false;

    fillList(root, '[data-nj-story-item]', stories, function (slot, story) {
      setImage(slot, '[data-nj-story-avatar]', story.avatarImage, story.studentName);
      setText(slot, '[data-nj-story-quote]', story.quote);
      setText(slot, '[data-nj-story-name]', story.studentName);
      setText(slot, '[data-nj-story-course]', story.course);
    });
  }

  function renderAccessories(root, config) {
    fillList(root, '[data-nj-accessory-item]', config.accessories, function (slot, item) {
      setImage(slot, '[data-nj-accessory-image]', item.image, item.title);
      setText(slot, '[data-nj-accessory-title]', item.title);
      setText(slot, '[data-nj-accessory-desc]', item.description);
    });
  }

  function renderFaq(root, config) {
    fillList(root, '[data-nj-faq-item]', config.faq, function (slot, item) {
      setText(slot, '[data-nj-faq-question-text]', item.question);
      setText(slot, '[data-nj-faq-answer]', item.answer);
    });

    bindFaqAccordion(root);
  }

  function bindFaqAccordion(root) {
    var faqList = root.querySelector('[data-nj-faq-list]');
    if (!faqList || faqList.dataset.njBound) return;

    faqList.dataset.njBound = 'true';

    faqList.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-nj-faq-question]');
      if (!trigger) return;

      var item = trigger.closest('[data-nj-faq-item]');
      if (!item) return;

      var isOpen = item.getAttribute('data-nj-open') === 'true';

      faqList.querySelectorAll('[data-nj-faq-item]').forEach(function (other) {
        other.setAttribute('data-nj-open', 'false');
      });

      if (!isOpen) {
        item.setAttribute('data-nj-open', 'true');
      }
    });
  }

  function renderFinalCta(root, config) {
    var finalCta = config.finalCta;
    if (!finalCta) return;

    setText(root, '[data-nj-final-title]', finalCta.title);
    setText(root, '[data-nj-final-subtitle]', finalCta.subtitle);
    setLink(root, '[data-nj-final-primary]', finalCta.primaryCtaUrl, finalCta.primaryCtaText);
    setLink(root, '[data-nj-final-secondary]', finalCta.secondaryCtaUrl, finalCta.secondaryCtaText);
  }

  /* ---------------------------------------------------------
     Public entry point
     --------------------------------------------------------- */

  function render(config, root) {
    if (!config || !root) return;

    renderAccentTheme(root, config);
    renderHero(root, config);
    renderBenefits(root, config);
    renderCoupon(root, config);
    renderEmi(root, config);
    renderServiceCenter(root, config);
    renderSupport(root, config);
    renderPlacement(root, config);
    renderSuccessStories(root, config);
    renderAccessories(root, config);
    renderDelivery(root, config);
    renderFaq(root, config);
    renderFinalCta(root, config);
  }

  return {
    render: render
  };
})();
